import { computed, ref, watch } from 'vue';
import { useAuthStore } from '@/stores/auth.ts';
import { useUserStore } from '@/stores/user.ts';
import { isMerchantAreaEnabled } from '@/utils/merchantArea.ts';

// The dashboard must never wait on mercury: past this delay the tile is simply not shown.
const ACCESS_TIMEOUT_MS = 5000;

/**
 * Asks mercury whether this account may open merchant-area. Only Monizze staff with the fridom `admin` role get
 * support access, and uzzer cannot see fridom roles, so mercury decides. Any error or timeout means "no".
 */
export const fetchMerchantAreaAccess = async (token: string): Promise<boolean> => {
	const baseUrl: string | undefined = import.meta.env.VITE_MERCURY_API_URL;
	if (!baseUrl || !token) {
		return false;
	}

	const controller = new AbortController();
	const timeout = setTimeout(() => controller.abort(), ACCESS_TIMEOUT_MS);
	try {
		const response = await fetch(`${baseUrl}/api/v1/merchant-area/me/access`, {
			method: 'GET',
			headers: { Accept: 'application/json', Authorization: `Bearer ${token}` },
			signal: controller.signal,
		});
		if (!response.ok) {
			return false;
		}
		const json = await response.json();
		return json?.data?.can_open === true;
	} catch {
		return false;
	} finally {
		clearTimeout(timeout);
	}
};

/**
 * Whether the dashboard shows the "Merchant area" tile:
 * - an account with a merchant_user_id sees it straight away;
 * - a Monizze (fridom) account without one sees it only if mercury says it can open merchant-area (one call at most);
 * - never on the Luxembourg tenant, nor when VITE_MERCHANT_AREA_ENABLED is off (then mercury is not asked either).
 */
export const useMerchantAreaTile = () => {
	const authStore = useAuthStore();
	const userStore = useUserStore();

	const enabled = isMerchantAreaEnabled();
	const supportCanOpen = ref(false);
	let accessRequested = false;

	watch(
		() => [userStore.user.merchant_user_id, userStore.user.fridom_user_id, authStore.isLux] as const,
		async ([merchantUserId, fridomUserId, isLux]) => {
			if (!enabled || accessRequested || merchantUserId || !fridomUserId || isLux) {
				return;
			}
			accessRequested = true;
			supportCanOpen.value = await fetchMerchantAreaAccess(authStore.token);
		},
		{ immediate: true },
	);

	const showMerchantAreaTile = computed(() => {
		if (!enabled || authStore.isLux) {
			return false;
		}
		if (userStore.user.merchant_user_id) {
			return true;
		}
		return Boolean(userStore.user.fridom_user_id) && supportCanOpen.value;
	});

	return { showMerchantAreaTile };
};
